/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  FormEvent,
  forwardRef,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  CreateJob,
  DownloadJobTasks,
  DownloadJobType,
  EntityStatus,
  GenericObject,
  JobConflictResolution,
  PlatformIdTypes
} from '@ion/core/src/types/entities';
import {SmartSearch} from '@ion/core/src/types/services';
import {getArrayDuplicates} from '@ion/core/src/utils/ArrayUtil';
import {smoothScrollByRef} from '@ion/core/src/utils/BrowserUtils';
import {DATETIME_UNDERSCORE_REGEX, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {getDevicePlatFormIds} from '@ion/core/src/utils/DeviceFileUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {isANumber} from '@ion/core/src/utils/NumberUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {getCustomWorkBook} from '@ion/core/src/xlsx/ExcelHelpers';
import {Alert} from '@ion/dls/src/components/alert/Alert';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {usePlatforms} from '@ion/query/src/hooks/graphql/usePlatforms';
import {useDeviceAppIds} from '@ion/query/src/hooks/useDeviceAppIds';
import {useDeviceTypes} from '@ion/query/src/hooks/useDeviceTypes';
import {useGetDevices} from '@ion/query/src/hooks/useGetDevices';
import {useS3BucketUrl} from '@ion/query/src/hooks/useS3BucketUrl';
import {DevicePlatformQueryParams} from '@ion/services/src/graphql/GraphQL';
import {Platform} from '@ion/services/src/graphql/types';
import {
  getPlatformIds,
  getSearchQueryString,
  hasValidationWarningCode,
  normalizeAppIds,
  normalizeJobTasks
} from '../components/jobs/helpers';
import {getRemovedDeviceIds} from '../components/jobs/JobConflictUtils';
import JobsConflictsTable from '../components/jobs/JobsConflictsTable';
import {JobDeviceFileTypeChooserPermissionsProps} from '../components/jobs/JobTaskDeviceFileTypeChooser';
import {JobTasksView} from '../components/jobs/JobTasksView';
import NoConflictsFoundAlert from '../components/jobs/NoConflictsFoundAlert';
import {useFilePermissionsJobCreation} from '../components/jobs/useFilePermissionsJobCreation';
import useGetJobConflicts from '../components/jobs/useGetJobConflicts';
import useJobConflictResolver from '../components/jobs/useJobConflictResolver';
import {useJobCreateMaxExceeded} from '../components/jobs/useJobCreateMaxExceeded';
import {useJobCreateState} from '../components/jobs/useJobCreateState';
import {useCurrentGlobalGroup} from '../hooks/useCurrentGlobalGroup';
import {useSanitizeAccount} from '../hooks/useSanitizeAccount';
import {DEFAULT_SMART_SEARCH_QUERY} from '../pages/devices/state';
import {devicesGridSearchSelector} from '../pages/devices/state-selectors';
import {SearchFields} from '../pages/devices/types';
import {setIsConflictsChecked} from './store/store';
import {extractUniqueTaskIds} from './BulkSelectEntitiesEditorUtils';
import CreateJobDetailsFormError from './CreateJobDetailsFormError';
import {BulkEntitiesImportParamsType} from './useBulkSelectedEntitiesImport';
import {useGridDeviceSelection} from './useGridDeviceSelection';

export interface CreateJobDetailsFormProps extends
  JobDeviceFileTypeChooserPermissionsProps {
  taskNickName: string;
  description?: string;
  entitySelectionFormState?: GenericObject;
  setTaskNickNameAction: (action: string) => void;
  onSave: (details: CreateJobDetails) => void;
  selectedEntitiesCount?: number;
  selectedIdentifiers?: string[];
  isAllEntitiesSelected?: boolean;
  selectedIdentifierType?: string;
  showInfoMsg?: boolean;
  hasFile?: boolean;
  isAllDevicesSelected?: boolean;
  isIdentifiersPasted?: boolean;
  isAllDevSelected?: boolean;
  customFileUpload?: boolean;
  userId?: number;
  isSelectedFromGrid?: boolean;
  bulkImportParams?: BulkEntitiesImportParamsType;
  jobSearchEngineEnabled?: boolean;
  noConflictsDevices?: string[];
  setNoConflictsDevices?: Function;
  setRemovedConflictDeviceIds?: Function;
  setIsCancelLoading?: Function;
  setIsProcessButtonDisabled?: Function;
}

export type CreateJobDetails = {
  job: CreateJob;
}

const useStyles = makeStyles(() => ({
  form : {
    padding : '0 35px 18px 35px'
  },
  dependentTaskForm : {
    transition : '300ms cubic-bezier(0.4, 0.0, 0.2, 1)',
    maxWidth   : 'calc(100% - 150px)',
    marginLeft : 'auto'
  },
  filtersError : {
    ...dlsAlt('typography', 'label2'),
    letterSpacing : '0px',
    color         : dlsColor('caRed'),
    marginBottom  : '5px'
  }
}));

/**
 * Returns normalized app ids array if enablePlatformApis is true, If not returns provided deviceAppIds.
 */
export const getDeviceAppIds = (devicesAppIds: Platform[], deviceAppIds: string[],
  enablePlatformApis: boolean = false) => {
  if (enablePlatformApis) {
    const appIds = devicesAppIds.filter(({platformName}) => isANumber(platformName));
    const valid  = getDevicePlatFormIds(appIds, true)?.length;
    if (valid) {
      return normalizeAppIds(getDevicePlatFormIds(appIds, true));
    }
  }
  return deviceAppIds;
};

/**
 * Returns filtered platformIds if enablePlatformApis is true, If not returns provided platformIds.
 */
export const getDeviceVbusIds = (devicesVbusIds: Platform[], platformIds: number[],
  enablePlatformApis: boolean = false) => {
  if (enablePlatformApis) {
    const valid = getDevicePlatFormIds(devicesVbusIds, false, true)?.length;
    if (valid) {
      return getDevicePlatFormIds(devicesVbusIds, false, true);
    }
  }
  return platformIds;
};

export const getQueries = (search, devicesGridSearch, filters) => {
  let customQuery = null;
  const isCustomQuery = !!devicesGridSearch?.query;
  let queryString: string = DEFAULT_SMART_SEARCH_QUERY;
  if (!isCustomQuery) {
    queryString = getSearchQueryString(search as SmartSearch);
  } else if (filters?.length) {
    customQuery = devicesGridSearch?.query;
  }

  return {customQuery, queryString};
};

/**
 * Re-usable form with the details for creating a job.
 */
export const CreateJobDetailsForm = forwardRef(({
  onSave,
  userId,
  Features,
  taskNickName,
  description,
  entitySelectionFormState,
  setTaskNickNameAction,
  selectedEntitiesCount,
  selectedIdentifiers,
  bulkImportParams,
  setIsCancelLoading,
  setNoConflictsDevices,
  setRemovedConflictDeviceIds,
  selectedIdentifierType,
  setIsProcessButtonDisabled,
  hasFile = false,
  isAllDevSelected = false,
  customFileUpload = false,
  isSelectedFromGrid = false,
  isIdentifiersPasted = false,
  isAllDevicesSelected = false,
  jobSearchEngineEnabled = false
}: CreateJobDetailsFormProps, ref) => {
  const classes           = useStyles();
  const globalGroup       = useCurrentGlobalGroup();
  const account           = useSanitizeAccount(globalGroup);
  const accountId         = entityId(account);
  const {deviceTypes}     = useDeviceTypes();
  const [s3Key, setS3Key] = useState('');
  const identifier        = selectedIdentifierType === 'id' ? 'deviceId' : selectedIdentifierType;
  const hasIMEI2          = isEqual(identifier, SearchFields.IMEI2);
  const devicesGridSearch = useSelector(devicesGridSearchSelector);
  const errorRef          = useRef(null);
  const {
    selectedDevices,
    includeSubAccounts,
    areTotalRowsSelected,
    totalRowsCount,
    search,
    filters
  } = useGridDeviceSelection();
  const {
    isLoading: isDevicesLoading,
    results: initialDevices
  } = useGetDevices({
    queryParams : {
      accountId,
      ...getQueries(search, devicesGridSearch, filters),
      includeSubGroups : includeSubAccounts
    },
    enabled : isAllDevSelected
  });

  const {uploadFile, isUploadingFile} = useS3BucketUrl();

  useEffect(() => {
    if (hasFile || customFileUpload) {
      const customWorkBook = customFileUpload && getCustomWorkBook(selectedIdentifiers, 'device');
      uploadFile({
        actionType : 'createJob',
        workbook   : customFileUpload ? customWorkBook : bulkImportParams?.workbook,
        entityType : 'device',
        userId,
        accountId
      }, {
        onSuccess : (s3DownloadKey) => {
          setS3Key(s3DownloadKey);
        },
        onError : (err) => {
          error(translate('common:maxDevices.uploadError'), err);
        }
      });
    }
  }, []);

  const getPayload = (type, returnPlatformId = false): DevicePlatformQueryParams => {
    if (isAllDevicesSelected) {
      return {
        search : {
          filter : {
            accountId,
            ...(includeSubAccounts && {hierLevel: 'ALL'}),
            platformTypeId : type
          }
        },
        fields : [returnPlatformId ? 'platformId' : 'platformName']
      };
    } else if (hasFile || customFileUpload) {
      if (!isUploadingFile) {
        return {
          search : {
            filter : {
              accountId,
              entityIdentifierS3Path : s3Key,
              entityIdentifierType   : identifier,
              ...(includeSubAccounts && {hierLevel: 'ALL'}),
              platformTypeId         : type
            }
          },
          fields : [returnPlatformId ? 'platformId' : 'platformName']
        };
      }
    } else if (isAllDevSelected) {
      return {
        search : {
          filter : {
            accountId,
            ...(includeSubAccounts && {hierLevel: 'ALL'}),
            [`${identifier}`] : initialDevices?.map(({id}) => id),
            platformTypeId    : type
          }
        },
        fields : [returnPlatformId ? 'platformId' : 'platformName']
      };
    } else {
      return {
        search : {
          filter : {
            accountId,
            ...(includeSubAccounts && {hierLevel: 'ALL'}),
            [`${identifier}`] : selectedIdentifiers,
            platformTypeId    : type
          }
        },
        fields : [returnPlatformId ? 'platformId' : 'platformName']
      };
    }
  };

  const enablePlatformApis = useMemo(() => !isDevicesLoading && (isSelectedFromGrid || isAllDevSelected ||
    (customFileUpload ? !isEmpty(s3Key) && !hasIMEI2 : isIdentifiersPasted && !hasIMEI2) ||
    (hasFile && !isEmpty(s3Key) && !hasIMEI2)), [
    isDevicesLoading,
    isSelectedFromGrid,
    isAllDevicesSelected,
    customFileUpload,
    s3Key,
    hasIMEI2,
    isIdentifiersPasted,
    hasFile
  ]);

  const {
    isFetching,
    isError,
    isFetched,
    errorResult,
    statusCode,
    resetError,
    platforms: fileTypes
  } = usePlatforms(getPayload(PlatformIdTypes.FILES, true), enablePlatformApis);

  const {platforms: devicesAppIds} = usePlatforms(getPayload(PlatformIdTypes.APP_IDS), enablePlatformApis);
  const {platforms: vbusIds}       = usePlatforms(getPayload(PlatformIdTypes.VBUS, true), enablePlatformApis);

  const {
    tasks,
    setTasks,
    isFileNameMissing,
    setIsFileNameMissing,
    isDuplicateFile,
    setIsDuplicateFile,
    isTaskGridEmpty,
    setIsTaskGridEmpty,
    setIsMaxDeviceSelected,
    isMaxDeviceSelected,
    setFilteredDeviceFileTypes,
    filteredDeviceFileTypes
  } = useJobCreateState({});

  const [conflictsChecked, setConflictsChecked]         = useState(false);
  const [readyToSubmit, setReadyToSubmit]               = useState(false);
  const [conflicts, setConflicts]                       = useState([]);
  const [selectedOptions, setSelectedOptions]           = useState({});
  const [finalDevs, setFinalDevices]                    = useState([]);
  const [showFileWarningDialog, setShowFileWarningDialog] = useState(false);
  const warningConfirmedRef                              = useRef(false);
  const shouldBypassConflictsCheck              = jobSearchEngineEnabled &&
  ((filters?.length && areTotalRowsSelected && totalRowsCount > 1000) || hasFile ||
  (!isSelectedFromGrid && !areTotalRowsSelected &&
    !(selectedIdentifierType === 'esn' || selectedIdentifierType === 'serialNumber')));
  const getDevicesfromIds = (identifiers, identifierType) => identifiers?.map(id => ({
    [`${identifierType}`] : id,
    ...(identifierType === 'serialNumber' && {category: 'MDT'})
  }));

  const has400Error = useMemo(() =>
    statusCode === 400 || statusCode === 502 || (isError && isUndefined(statusCode)), [statusCode, isError]);

  const {
    getDeviceConflicts,
    isConflictsLoading,
    setShowConflicts,
    showConflicts
  } = useGetJobConflicts({
    accountId,
    devices : initialDevices?.length ? initialDevices : selectedDevices?.length
      ? selectedDevices : getDevicesfromIds(selectedIdentifiers, selectedIdentifierType),
    searchEntireAccount : isAllDevSelected ? false : areTotalRowsSelected,
    tasks
  });

  const {
    cancelSelectedConflicts
  } = useJobConflictResolver({
    areTotalRowsSelected,
    selectedOptions,
    accountId,
    conflicts,
    devices : finalDevs
  });

  useEffect(() => {
    const callCancelConflicts = async() => {
      if (finalDevs) {
        await cancelSelectedConflicts();
      }
    };
    callCancelConflicts();
  }, [finalDevs]);

  const {
    customQuery,
    queryString,
    doIncludeSubAccounts,
    usingSelectedDevicesAppIds
  } = getDeviceAppIdsParams({
    includeSubAccounts,
    originalFilters           : filters,
    customQuery               : devicesGridSearch,
    areTotalRowsSelected,
    totalRowsCount,
    totalSelectedDevicesCount : selectedDevices?.length || 0,
    search
  });

  let {deviceAppIds: appIds} = useDeviceAppIds({
    accountId,
    customQuery,
    queryString,
    includeSubAccounts : doIncludeSubAccounts
  });

  if (usingSelectedDevicesAppIds && selectedDevices?.length) {
    appIds = [];
    selectedDevices.forEach(({extensions}) => {
      const appId = extensions?.appId;
      if (appId) {
        appIds.push(String(appId));
      }
    });
  };

  const deviceAppIds = useMemo(() => normalizeAppIds(appIds), [safeJSONStringify(appIds)]);

  const platformIds      = getPlatformIds(selectedDevices);
  const isDeviceSelected = !!selectedDevices.length ||
    !!entitySelectionFormState?.entityIdentifiers?.value?.length ||
    !!entitySelectionFormState?.file?.value?.importParams?.items?.length;

  useFilePermissionsJobCreation({
    Features,
    deviceTypes,
    enablePlatformApis,
    isFetching,
    deviceFileTypes : fileTypes,
    setFilteredDeviceFileTypes,
    has400Error
  });

  const {maxLength, isMaxExceeded} = useJobCreateMaxExceeded({
    allRowsSelected : areTotalRowsSelected,
    rowCount        : totalRowsCount,
    selectedCount   : selectedEntitiesCount
  });

  useEffect(() => {
    const jobTasks = tasks?.tasks;
    const firstJobTask = jobTasks?.[0]?.task || {};
    const {
      deviceType = '',
      fileType = '',
      fileNameUI = ''
    } = firstJobTask;
    const jobTaskCount = jobTasks?.length || 0;
    let jobTasksLabel = translate('common:prepositions.with');
    if (jobTaskCount <= 1) {
      jobTasksLabel += ` ${deviceType} ${fileType} ${fileNameUI}`;
    } else {
      jobTasksLabel += ` ${jobTaskCount} ${translate('common:entityLabels.files')}`;
    }
    setTaskNickNameAction(jobTasksLabel.trim());
  }, [tasks]);

  const getFinalDevices = (devices) => {
    const selectedDevs = initialDevices?.length ? initialDevices : selectedDevices?.length ? selectedDevices : devices;
    return selectedDevs?.filter(device => {
      const shouldRemove = conflicts?.some(conflictGroup => {
        return conflictGroup?.some(conflict => {
          const conflictKey = `${conflict?.id}-${conflict?.task}`;
          if (selectedOptions?.[conflictKey]?.value !== JobConflictResolution.REMOVE) {
            return false;
          }
          const matchingDeviceFound = conflict?.devices?.some(conflictDevice => {
            return conflictDevice?.deviceIdentifier === device?.esn;
          });
          return matchingDeviceFound;
        });
      });
      return !shouldRemove;
    });
  };

  const onSubmit = async function(e?: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();
    const existingTasks: DownloadJobTasks = tasks?.tasks;
    const isTaskGridEmpty = existingTasks?.length === 0;
    let isValid = true;
  
    if (isTaskGridEmpty) {
      isValid = false;
    }
    setIsTaskGridEmpty(isTaskGridEmpty);
  
    if (!onUpdateTasks(tasks)) {
      isValid = false;
    }
  
    const {requiredField} = checkTasks(tasks);
    setIsFileNameMissing(requiredField);
    if (requiredField) {
      isValid = false;
    }
  
    if (isMaxExceeded) {
      setIsMaxDeviceSelected(true);
      isValid = false;
    }

    if (!isValid) {
      smoothScrollByRef(errorRef);
      return;
    }

    if (!warningConfirmedRef.current && hasValidationWarningCode(tasks?.tasks)) {
      setShowFileWarningDialog(true);
      return;
    }
  
    if (isValid) {
      let finalDevices = initialDevices?.length ? [...initialDevices] : selectedDevices?.length
        ? [...selectedDevices]
        : getDevicesfromIds(selectedIdentifiers, selectedIdentifierType);
  
      if (jobSearchEngineEnabled && !shouldBypassConflictsCheck) {
        if (!conflictsChecked) {
          setIsCancelLoading(true);
          const devicesWithConflicts = await getDeviceConflicts();
          setConflictsChecked(true);
          setIsConflictsChecked(true);
          setIsCancelLoading(false);
  
          if (devicesWithConflicts.length > 0) {
            setConflicts(devicesWithConflicts);
            setShowConflicts(true);
          } else {
            setConflicts([]);
            setShowConflicts(false);
            setIsProcessButtonDisabled(false);
            setReadyToSubmit(true);
          }
          // We stop here if conflictsChecked === false
          return;
        }
  
        // Second submission after conflictsChecked === true
        if (conflicts.length > 0) {
          const totalConflictsCount = conflicts.reduce(
            (count, group) => count + (Array.isArray(group) ? group?.length : 0),
            0);
          const areConflictsResolved = extractUniqueTaskIds(selectedOptions)?.length === totalConflictsCount;
          if (!areConflictsResolved) {
            setShowConflicts(true);
            return;
          }
        }
  
        // Filter out devices marked to be removed
        finalDevices = getFinalDevices(finalDevices);
        setFinalDevices(finalDevices);
        setNoConflictsDevices(
          finalDevices.map(
            (device) => device?.id || device?.esn || device?.serialNumber
          )
        );
        setRemovedConflictDeviceIds?.(getRemovedDeviceIds(conflicts, selectedOptions));
        setIsCancelLoading(true);
      }
  
      setIsCancelLoading(false);
      const doSave = () => {
        onSave?.(createDetails({
          account,
          taskNickName,
          description,
          tasks
        }));
        setIsConflictsChecked(false);
        warningConfirmedRef.current = false;
      };
      doSave();
    }
  };

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const onUpdateTasks = useCallback(function(tasks: CreateJob) {
    let isValid: boolean = true;
    if (!isNil(tasks)) {
      const {requiredField, fileNames} = checkTasks(tasks);
      if (requiredField) {
        isValid = false;
      }
      !requiredField && setIsFileNameMissing(requiredField);

      const duplicates  = getArrayDuplicates(fileNames);
      const isDuplicate = !!duplicates.length;
      if (isDuplicate) {
        isValid = false;
      }
      setIsDuplicateFile(isDuplicate);
    } else {
      isValid = false;
    }
    return isValid;
  }, []);

  const onDismiss = useCallback(() => {
    setIsMaxDeviceSelected(false);
  }, []);

  const onFileWarningConfirm = useCallback(() => {
    warningConfirmedRef.current = true;
    setShowFileWarningDialog(false);
    onSubmit();
  }, [onSubmit]);

  const onFileWarningDismiss = useCallback(() => {
    setShowFileWarningDialog(false);
    warningConfirmedRef.current = false;
  }, []);

  const getSelectedDevices = () => {
    if (isAllDevSelected) {
      return initialDevices;
    }
    if (areTotalRowsSelected && !filters?.length) {
      return [];
    }
    if (selectedDevices?.length) {
      return selectedDevices;
    } else {
      return getDevicesfromIds(selectedIdentifiers, selectedIdentifierType);
    }
  };

  const dependentTaskExists = useMemo(() => {
    const hasDependency = (task) => {
      const dep = task?.metaData?.dependency;
      if (!dep) return false;
      if (Array.isArray(dep)) return dep?.length > 0;
      return !isNil(dep.index);
    };
    return tasks?.tasks?.some(task => hasDependency(task?.task)) ?? false;
  }, [tasks]);

  const onCreateJobBackClick = useCallback(() => {
    setReadyToSubmit(false);
    setConflictsChecked(false);
    setIsConflictsChecked(false);
  }, []);

  return (
    <div className={`${classes.form} ${dependentTaskExists && classes.dependentTaskForm}`}>
      <Render.If condition={readyToSubmit || isConflictsLoading}>
        <NoConflictsFoundAlert conflictsDetectionLoading={isConflictsLoading}
          readyToSubmit={readyToSubmit}
          onBackClick={onCreateJobBackClick}/>
      </Render.If>

      <Render.If condition={!readyToSubmit && !showConflicts && !isConflictsLoading}>
        <Render.If condition={jobSearchEngineEnabled && shouldBypassConflictsCheck}>
          <Alert type='warning'
            message={translate('jobcreate:conflict.warning.check')}
            gutters={false}
            constrained />
        </Render.If>
        <JobTasksView tasks={tasks}
          isFetching={isUploadingFile || isFetching || isDevicesLoading}
          errorRef={errorRef}
          showInfoMsg={areTotalRowsSelected && !!filters?.length && totalRowsCount > 1000}
          hasIMEI2={hasIMEI2}
          setTasks={setTasks}
          onUpdateTasks={onUpdateTasks}
          deviceTypes={filteredDeviceFileTypes}
          deviceAppIds={getDeviceAppIds(devicesAppIds, deviceAppIds, enablePlatformApis)}
          accountId={accountId}
          includeSubAccounts={doIncludeSubAccounts}
          isFileNameMissing={isFileNameMissing}
          isDuplicateFile={isDuplicateFile}
          setIsTaskGridEmpty={setIsTaskGridEmpty}
          isTaskGridEmpty={isTaskGridEmpty}
          devicePlatformIds={getDeviceVbusIds(vbusIds, platformIds, enablePlatformApis)}
          isDevicesSelected={isDeviceSelected}
          isStandalone
          isLimitEnabled
          tasksInitialized />
      </Render.If>

      <Render.If condition={isConflictsLoading || showConflicts}>
        <JobsConflictsTable isConflictsLoading={isConflictsLoading}
          selectedDevices={getSelectedDevices()}
          setShowConflicts={setShowConflicts}
          jobName={taskNickName}
          conflicts={conflicts}
          onCreateJobBackClick={onCreateJobBackClick}
          setSelectedOptions={setSelectedOptions}
          selectedOptions={selectedOptions}
          jobSearchEngineEnabled={jobSearchEngineEnabled}
          setIsProcessButtonDisabled={setIsProcessButtonDisabled}
          selectedTasks={tasks} />
      </Render.If>
      <Render.If condition={showFileWarningDialog}>
        <UserNotificationDialog show
          icon={<WarningIcon />}
          title={translate('jobcreate:fileWarning.title')}
          message={translate('jobcreate:fileWarning.message')}
          actionLabel={translate('jobcreate:fileWarning.confirm')}
          dismissLabel={translate('jobcreate:fileWarning.cancel')}
          onAction={onFileWarningConfirm}
          onDismiss={onFileWarningDismiss}
          capitalize />
      </Render.If>
      <Render.If condition={isMaxDeviceSelected}>
        <UserNotificationDialog show
          capitalize
          title={translate('common:maxDevices.information')}
          message={translate('common:maxDevices.identifierMessage', {maxDevices: maxLength})}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss} />
      </Render.If>
      <CreateJobDetailsFormError isFetched={isFetched}
        errorResult={errorResult}
        resetError={resetError}
        isError={isError}
        fileTypes={fileTypes} />
    </div>
  );
});

/**
 * Retrieves task file information for validation.
 */
const checkTasks = (tasks: CreateJob) => {
  const requiredField = [];
  const fileNames     = [];
  let rows;
  tasks?.tasks?.forEach((data, index) => {
    rows = data?.task;
    if (!rows.fileName?.length) {
      requiredField.push(index);
    } else {
      fileNames.push(rows.metaData.file);
    }
  });
  return {requiredField: !!requiredField.length, fileNames};
};

/**
 * Determines the parameters to be used for fetching app IDs for the chosen set of devices.
 *
 * If device selection has been done via a file or via a pasted list of identifiers, then
 * no selections have been done via the devices grid, so areTotalRowsSelected will be false
 * AND the individual selected device count (totalSelectedDevicesCount) will be zero. In this
 * case, we should make sure to include all sub-accounts in the account to make sure we find
 * app IDs for all the devices in the file or pasted list.
 *
 * If device selection has been done via the devices grid, then if all devices have been
 * selected (areTotalRowsSelected is true), totalRowsCount contains the selected device
 * count; otherwise totalSelectedDeviceCount contains the individually selected device count.
 *
 * If this count is over 1000, we want to get all app IDs in the account (using device search
 * criteria = DEFAULT_SMART_SEARCH_QUERY = 'id: *' to get the aggregated list of app IDs).
 * Otherwise:
 *   - If all devices have been selected, we want to use the device search criteria to get
 *     the aggregated list of app IDs for those devices.
 *   - If devices have been individually selected:
 *     - If the count is over 100 (platform limit), we need to use the
 *       device search criteria (long list of individual device IDs) to get the aggregated
 *       list of app IDs (since we don't have all the individual devices).
 *     - Otherwise, we can just get the app IDs from the individual devices.
 */
const getDeviceAppIdsParams = ({
  originalFilters,
  customQuery: newCustomQuery,
  includeSubAccounts,
  areTotalRowsSelected,
  totalRowsCount,
  totalSelectedDevicesCount,
  search
}) => {
  let customQuery                         = null;
  const isCustomQuery                     = !!newCustomQuery?.query;
  let queryString: string                 = DEFAULT_SMART_SEARCH_QUERY;
  let doIncludeSubAccounts: boolean       = includeSubAccounts;
  let usingSelectedDevicesAppIds: boolean = false;

  if (areTotalRowsSelected) {
    if (!isCustomQuery) {
      if (search?.smartSearch?.queryString?.includes(SearchFields.APP_ID)) {
        usingSelectedDevicesAppIds = false;
        queryString = getSearchQueryString(search as SmartSearch);
      } else if (totalRowsCount <= 1000) {
        queryString = getSearchQueryString(search as SmartSearch);
      }
    } else if (originalFilters?.length) {
      customQuery = newCustomQuery;
    }
  } else {
    if (totalSelectedDevicesCount === 0) {
      doIncludeSubAccounts = true;
    } else if (totalSelectedDevicesCount <= 1000) {
      if (totalSelectedDevicesCount > 100) {
        queryString = getSearchQueryString(search as SmartSearch);
      } else {
        usingSelectedDevicesAppIds = true;
      }
    }
  }

  return {customQuery, queryString, doIncludeSubAccounts, usingSelectedDevicesAppIds};
};

/**
 * Builds the job name from the specified task nickname. The task nickname is
 * not required to be unique; however, the job name IS required to be unique.
 * Therefore, the job name is set to the task nickname and a UTC timestamp is
 * included to ensure uniqueness.
 */
const createJobName = (taskNickName: string): string => {
  const date = new Date();
  const timestamp = timeFormatter(date, 'YYYYMMDD_HHmmss', true);
  let cleanedName = taskNickName?.trim() || '';
  const existingDateMatch = cleanedName.match(DATETIME_UNDERSCORE_REGEX);
  if (existingDateMatch) {
    if (cleanedName.endsWith(existingDateMatch[0])) {
      return cleanedName;
    } else {
      cleanedName = cleanedName.replace(DATETIME_UNDERSCORE_REGEX, '').trim().replace(/[,;]\s*$/, '');
      return `${cleanedName}; ${existingDateMatch[0]}`;
    }
  }

  return `${cleanedName}; ${timestamp}`;
};

/**
 * Builds the action details structure which will ultimately be passed to
 * the bulk action API. The job name will be updated from the bulk activity
 * task nickname.
 */
const createDetails = ({
  account,
  taskNickName,
  description,
  tasks
}): CreateJobDetails => {
  const detailsTasks   = cloneDeep(tasks?.tasks);
  const job: CreateJob = {
    status         : EntityStatus.Enabled,
    progressStatus : DownloadJobType.Pending,
    name           : createJobName(taskNickName),
    tasks          : detailsTasks,
    description,
    account
  };
  return {job: normalizeJobTasks(job)};
};

export default memo(CreateJobDetailsForm);
